import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, PaperPlaneRight, Heart, PencilSimple, Trash, ArrowBendDownRight } from "@phosphor-icons/react";
import { reelsApi } from "@/lib/apiClient";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function timeAgo(iso) {
    if (!iso) return "";
    const delta = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
    if (delta < 60) return `${delta}s`;
    const mins = Math.round(delta / 60);
    if (mins < 60) return `${mins}m`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.round(hours / 24)}d`;
}

export default function ReelCommentsDrawer({ reel, open, onClose, onCountChange }) {
    const { user } = useAuth() || {};
    const [comments, setComments] = useState([]);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [replyTo, setReplyTo] = useState(null);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        if (!reel?.id) return;
        try {
            setComments(await reelsApi.comments(reel.id));
        } catch {
            setComments([]);
        }
    };

    useEffect(() => {
        if (!open || !reel) return;
        setText("");
        setReplyTo(null);
        setEditing(null);
        load();
    }, [open, reel?.id]);

    const rootComments = useMemo(() => comments.filter((comment) => !comment.parent_id), [comments]);
    const repliesOf = (id) => comments.filter((comment) => comment.parent_id === id);
    const startReply = (comment) => {
        setReplyTo(comment);
        setEditing(null);
        setText("");
    };
    const startEdit = (comment) => {
        setEditing(comment);
        setReplyTo(null);
        setText(comment.text || "");
    };

    const submit = async (event) => {
        event.preventDefault();
        if (!text.trim()) return;
        if (!user) return toast.error("Sign in to comment");
        setSending(true);
        try {
            if (editing) {
                const updated = await reelsApi.editComment(editing.id, text.trim());
                setComments((prev) => prev.map((comment) => (comment.id === editing.id ? updated : comment)));
                setEditing(null);
            } else {
                const created = await reelsApi.addComment(reel.id, text.trim(), replyTo?.id || null);
                setComments((prev) => [...prev, created]);
                onCountChange?.((comments.length || 0) + 1);
                setReplyTo(null);
            }
            setText("");
        } catch {
            toast.error("Could not post comment");
        } finally {
            setSending(false);
        }
    };

    const toggleLike = async (comment) => {
        if (!user) return toast.error("Sign in to like comments");
        setComments((prev) => prev.map((entry) => (
            entry.id === comment.id
                ? { ...entry, liked: !entry.liked, like_count: (entry.like_count || 0) + (entry.liked ? -1 : 1) }
                : entry
        )));
        try {
            await reelsApi.likeComment(comment.id);
        } catch {
            load();
        }
    };

    const deleteComment = async (comment) => {
        if (!window.confirm("Delete this comment?")) return;
        try {
            await reelsApi.removeComment(comment.id);
            const next = comments.filter((entry) => entry.id !== comment.id && entry.parent_id !== comment.id);
            setComments(next);
            onCountChange?.(next.length);
        } catch {
            toast.error("Could not delete comment");
        }
    };

    return (
        <AnimatePresence>
            {open && reel && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[95] flex items-end md:items-center justify-center p-0 md:p-4"
                    style={{ background: "rgba(5,10,17,0.84)", backdropFilter: "blur(10px)" }}
                    onClick={onClose}
                    data-testid="reel-comments-drawer"
                >
                    <motion.div
                        onClick={(event) => event.stopPropagation()}
                        initial={{ y: 32, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 24, opacity: 0 }}
                        className="w-full md:max-w-xl md:rounded-3xl overflow-hidden max-h-[88vh] flex flex-col"
                        style={{ background: "var(--navy)", border: "1px solid rgba(212,175,55,0.24)" }}
                    >
                        <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                            <div>
                                <div className="overline mb-1">Comments</div>
                                <div className="font-serif text-lg text-white leading-tight" data-testid="reel-comments-heading">on @{reel.user_handle}&rsquo;s reel</div>
                            </div>
                            <button onClick={onClose} className="w-9 h-9 rounded-full glass grid place-items-center" data-testid="reel-comments-close" aria-label="Close comments">
                                <X size={14} />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="reel-comments-list">
                            {rootComments.map((comment) => (
                                <div key={comment.id} className="space-y-3">
                                    <CommentItem
                                        comment={comment}
                                        me={user}
                                        onLike={toggleLike}
                                        onReply={startReply}
                                        onEdit={startEdit}
                                        onDelete={deleteComment}
                                        dataPrefix="reel-comment"
                                    />
                                    {repliesOf(comment.id).map((reply) => (
                                        <div key={reply.id} className="pl-6 border-l" style={{ borderColor: "rgba(212,175,55,0.18)" }}>
                                            <CommentItem
                                                comment={reply}
                                                me={user}
                                                onLike={toggleLike}
                                                onReply={startReply}
                                                onEdit={startEdit}
                                                onDelete={deleteComment}
                                                inReplyTo={comment.user_handle}
                                                dataPrefix="reel-comment"
                                            />
                                        </div>
                                    ))}
                                </div>
                            ))}
                            {comments.length === 0 && <div className="text-center py-10 text-white/50 text-sm">Be the first to comment.</div>}
                        </div>

                        {(replyTo || editing) && (
                            <div className="px-5 py-2 text-xs text-white/60 border-t flex items-center justify-between" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                                <span data-testid="reel-comment-context">{editing ? "Editing your comment" : `Replying to @${replyTo?.user_handle}`}</span>
                                <button
                                    onClick={() => { setReplyTo(null); setEditing(null); setText(""); }}
                                    className="hover:text-white"
                                    data-testid="reel-comment-cancel"
                                >
                                    Cancel
                                </button>
                            </div>
                        )}

                        <form onSubmit={submit} className="p-4 border-t flex gap-2" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                            <input
                                value={editing ? text || editing.text : text}
                                onChange={(event) => setText(event.target.value)}
                                placeholder={editing ? "Edit your comment…" : replyTo ? `Reply to @${replyTo.user_handle}…` : "Add a comment…"}
                                className="flex-1 bg-black/30 border rounded-full px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[color:var(--gold)]"
                                style={{ borderColor: "rgba(255,255,255,0.1)" }}
                                data-testid="reel-comment-input"
                            />
                            <button type="submit" disabled={sending || !user} className="btn-gold text-sm !py-2.5 !px-4" data-testid="reel-comment-submit" aria-label="Send comment">
                                <PaperPlaneRight size={14} weight="fill" />
                            </button>
                        </form>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function CommentItem({ comment, me, onLike, onReply, onEdit, onDelete, inReplyTo, dataPrefix }) {
    const isMine = me && comment.user_id === me.id;
    return (
        <div className="flex gap-3" data-testid={`${dataPrefix}-${comment.id}`}>
            <img src={comment.user_avatar || `https://api.dicebear.com/7.x/notionists/svg?seed=${comment.user_handle}`} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
            <div className="flex-1 min-w-0">
                <div className="text-xs flex items-center gap-2 flex-wrap">
                    <span className="font-medium" style={{ color: "var(--gold)" }} data-testid={`${dataPrefix}-handle-${comment.id}`}>@{comment.user_handle}</span>
                    <span className="text-white/40">·</span>
                    <span className="text-white/40">{timeAgo(comment.created_at)}</span>
                    {comment.edited_at && <span className="text-white/40 italic">edited</span>}
                    {inReplyTo && <span className="text-white/40">→ @{inReplyTo}</span>}
                </div>
                <div className="text-sm text-white/85 mt-1 leading-relaxed whitespace-pre-wrap break-words" data-testid={`${dataPrefix}-text-${comment.id}`}>{comment.text}</div>
                <div className="flex items-center gap-3 mt-1.5 text-[11px] text-white/50">
                    <button onClick={() => onLike(comment)} className="flex items-center gap-1 hover:text-[color:var(--gold)]" data-testid={`${dataPrefix}-like-${comment.id}`}>
                        <Heart size={12} weight={comment.liked ? "fill" : "regular"} color={comment.liked ? "#ff5f5f" : "currentColor"} />
                        {comment.like_count || 0}
                    </button>
                    <button onClick={() => { onReply(comment); }} className="flex items-center gap-1 hover:text-white" data-testid={`${dataPrefix}-reply-${comment.id}`}>
                        <ArrowBendDownRight size={12} /> Reply
                    </button>
                    {isMine && (
                        <>
                            <button onClick={() => onEdit(comment)} className="flex items-center gap-1 hover:text-white" data-testid={`${dataPrefix}-edit-${comment.id}`}>
                                <PencilSimple size={12} /> Edit
                            </button>
                            <button onClick={() => onDelete(comment)} className="flex items-center gap-1 hover:text-red-300" data-testid={`${dataPrefix}-delete-${comment.id}`}>
                                <Trash size={12} /> Delete
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}